import React, { createContext, useContext, useEffect, useState } from 'react';
import publicApi from '../services/publicApi';
import { logoService } from '../services/logoService';

type Theme = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: 'light' | 'dark';
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

interface ThemeData {
  nome: string;
}

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => {
    if (typeof window !== 'undefined') {
      return (localStorage.getItem('folio-theme') as Theme) || 'dark';
    }
    return 'dark';
  });
  const [resolvedTheme, setResolvedTheme] = useState<'light' | 'dark'>('dark');
  const [isLoading, setIsLoading] = useState(true);

  // Resolver tema (system -> light/dark)
  useEffect(() => {
    const resolve = () => {
      if (theme === 'system') {
        const systemDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
        setResolvedTheme(systemDark ? 'dark' : 'light');
      } else {
        setResolvedTheme(theme);
      }
    };

    resolve();

    // Listener para mudanças no sistema
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = () => theme === 'system' && resolve();
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, [theme]);

  // Aplicar classe dark no documento
  useEffect(() => {
    const root = document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(resolvedTheme);
    
    // Notificar logoService sobre mudança de tema
    logoService.switchLogoForTheme(resolvedTheme === 'dark');
  }, [resolvedTheme]);

  // Carregar dados básicos da empresa (apenas título; cores do tema não são mais aplicadas)
  useEffect(() => {
    const loadBrandTheme = async () => {
      try {
        const response = await publicApi.get('/theme');
        const themeData: ThemeData = response.data;

        // Atualizar título da página
        if (themeData.nome) {
          document.title = themeData.nome;
        }
      } catch (error) {
        console.warn('[THEME] Usando tema padrão:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadBrandTheme();
  }, []);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
    localStorage.setItem('folio-theme', newTheme);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-foreground"></div>
      </div>
    );
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
